import { Link } from '@mm/components';
import contactsCRUDActions, {
  ImportContactsOpts,
} from '@mm/state/actions/ContactsCRUDActions';
import AttributeModel from '@mm/state/model/AttributeModel';
import modalActions from '@mm/state/modules/modalsState/actions';
import {
  selectCurrentUserIsAdmin,
  selectSessionSelector,
} from '@mm/state/selectors/userSelector';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { getUniqId } from '@mm/utils';
import { ReactComponent as ArrowRightLongIcon } from 'assets/icons/arrow-right-long.svg';
import { ReactComponent as CheckmarkIcon } from 'assets/icons/green-check-circle.svg';
import classNames from 'classnames';
import { snakeCase } from 'lodash';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { NeedHelpLabel } from '../../../editor/features/common/components/NeedHelpLabel/NeedHelpLabel';
import {
  ButtonColor,
  PrimaryButton,
} from '../../buttons/PrimaryButton/PrimaryButton';
import Checkbox from '../../components/Checkbox';
import { OnClickElsewhere } from '../../components/OnClickElsewhere/OnClickElsewhere';
import PrimarySelect from '../../components/PrimarySelect/PrimarySelect';
import { Title } from '../../components/Title/Title';
import { useAttributeHandlers } from '../../helpers/widget-helpers/use-attribute-handlers';
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalProcessingBlinder,
} from '../modal-helpers';
import { CSVDropParser, ParsedData } from './CSVDropParser/CSVDropParser';
import './ImportContactListModal.pcss';
import { Routes } from '../../../routes/top-level-routes';
import { useImportContactsPermission } from '../../hooks/use-import-contacts-permission';
import {
  IImportContactListModal,
  IndexAttributeNameMap,
} from '@mm/state/modules/modalsState/props';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import {
  ArrowRightOutlined,
  CloseOutlined,
  WarningFilled,
  WarningOutlined,
} from '@ant-design/icons';

export const PHONE_ATTRIBUTE_NAME = 'PHONE';
export const EMAIL_ATTRIBUTE_NAME = 'EMAIL';
export const IGNORELIST = ['UNSUBSCRIBED'];

export const phoneAttribute = {
  id: getUniqId(),
  name: PHONE_ATTRIBUTE_NAME,
  system: false,
};

export const CSVMapper = ({
  columns = [],
  onUpdate,
  customDescriptiontext,
  hideColumnName,
  customDropdownAlign,
}: {
  columns: string[];
  onUpdate?: (map: IndexAttributeNameMap, isValid: boolean) => void;
  customDescriptiontext?: string;
  hideColumnName?: boolean;
  customDropdownAlign?: 'start' | 'center' | 'end';
}) => {
  const permissions = useImportContactsPermission();

  let { settableAttributes: attrs } = useAttributeHandlers(true);
  // some bots might not have 'PHONE' custom variable, BE requires one with such name
  const withPhone = attrs.some(({ name }) => name === PHONE_ATTRIBUTE_NAME);
  if (!withPhone) attrs = [...attrs, phoneAttribute];

  const [map, setMap] = useState<IndexAttributeNameMap>({});
  const [fMap, setfMap] = useState<IndexAttributeNameMap>({});

  const selectedNames = Object.values(map);
  const options = attrs
    .filter(attr => !selectedNames.includes(attr.name))
    .filter(attr => !IGNORELIST.includes(attr.name));

  const isMapValid = (map: IndexAttributeNameMap) =>
    [PHONE_ATTRIBUTE_NAME, EMAIL_ATTRIBUTE_NAME].some(attr =>
      Object.values(map).includes(attr),
    );

  const onAttributeSelected = (
    { name, system }: AttributeModel,
    index: number,
  ) => {
    const next = { ...map, [index]: name };
    setMap(next);

    // for system attr snake case code is used instead of name (BE requirement)
    // asks BE to use variable ids...
    const formattedMap = { ...fMap, [index]: system ? snakeCase(name) : name };
    setfMap(formattedMap);

    onUpdate && onUpdate(formattedMap, isMapValid(next));
  };

  const hasPhoneSelected = Object.values(map).some(
    v => v === PHONE_ATTRIBUTE_NAME,
  );

  const isError = !isMapValid(map);

  return (
    <>
      <p className="csv-mapper__description">
        {customDescriptiontext ||
          'Please assign an attribute to each of the fields.'}
      </p>
      {isError && (
        <div className="csv-mapper__error">
          <WarningFilled className="csv-mapper__error-icon" />
          Column(s) must be assigned to the Email attributes
        </div>
      )}
      <div className="csv-mapper">
        {!hideColumnName && (
          <div className="csv-mapper__header">
            <div className="csv-mapper__header-title">Column name</div>
            <div className="csv-mapper__header-title">Attribute name</div>
          </div>
        )}
        <div className="csv-mapper__rows">
          {columns.map((id: string, index: number) => (
            <div className="csv-mapper__row" key={id}>
              <div className="csv-mapper__column-name">{id}</div>
              <ArrowRightOutlined className="csv-mapper__arrow" />
              <div className="csv-mapper__attribute">
                <PrimarySelect
                  className="csv-mapper__attribute-selector"
                  dropdownClassName="csv-mapper__attribute-selector__dropdown"
                  options={options}
                  value={attrs.find(({ name }) => name === map[index + 1])}
                  onChange={v =>
                    onAttributeSelected(v as AttributeModel, index + 1)
                  }
                  showSearchFilter={true}
                  customDropdownStyles={{ zIndex: '99999999' }}
                  customDropdownAlign={customDropdownAlign}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {!permissions.canMapPhoneNumber && hasPhoneSelected && (
        <div className="csv-mapper__warning">
          <strong>Warning:</strong> Your account is not yet verified for
          uploading SMS contacts. Please email{' '}
          <a href="mailto:cs@customers.ai">cs@customers.ai</a> to enable this
          feature.
          <br />
        </div>
      )}
    </>
  );
};

export const useDefaultImportContactList = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const dispatch: Dispatch<StoreState> = useDispatch();

  const importContactList = async (
    opts: ImportContactsOpts,
    onSuccess?: IImportContactListModal['onSuccess'],
  ) => {
    setIsLoading(true);
    try {
      const res = await dispatch(
        contactsCRUDActions.importContactsFromCsv(opts),
      );
      if (onSuccess && res?.contactsCsv?.audience?.id) {
        return onSuccess({ audienceId: res.contactsCsv.audience.id as number });
      }
      setIsSuccess(true);
      return res?.contactsCsv;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    importContactList,
    isLoading,
    isSuccess,
  };
};

const exampleData = `first name, last name, phone number, email
larry, kim, +6175551111, larry@customers.ai`;
export const csvExample = encodeURI(
  `data:text/csv;charset=utf-8,${exampleData}`,
);

export const ImportContactListModal = ({
  className,
  closeModal,
  onSuccess,
  onSubmit,
  title = 'Import Contact List',
  helpLink = 'https://customers.ai/help/article/mu1q5ei8ji-import-sms-contact-list',
  csvExampleBody = csvExample,
  csvExampleName = 'phonenumbers.csv',
  withMapper = true,
}: IImportContactListModal) => {
  const session = useSelector(selectSessionSelector);
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const facebookPage = useSelector(activeFacebookPageSelector);
  const isSysOrCSAdmin = useSelector(selectCurrentUserIsAdmin);

  const {
    importContactList,
    isLoading: defaultIsLoading,
    isSuccess: defaultIsSucess,
  } = useDefaultImportContactList();

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const isLoading = defaultIsSucess || loading;
  const isSuccess = defaultIsSucess || success;

  const dispatch: Dispatch<StoreState> = useDispatch();

  const [isAgreementAccepted, setIsAgreementAccepted] = useState(false);
  const [isFirstRowHeader, setIsFirstRowHeader] = useState(!!withMapper);
  const [fileData, setFileData] = useState<{
    csvFile?: File;
    parsedData?: ParsedData;
  }>({});
  const [mapping, setMapping] = useState<{
    isValid: boolean;
    map?: IndexAttributeNameMap;
  }>({
    isValid: false,
  });

  const onCancelBtnClick = () => closeModal();

  const onDoneButtonClick = async () => {
    const formattedMap = mapping.map;

    if (fileData.csvFile) {
      const opts = {
        facebookPageId,
        file: fileData.csvFile,
        mapping: formattedMap as IndexAttributeNameMap,
        isFirstRowHeader,
      };

      if (typeof onSubmit === 'function') {
        await onSubmit(opts, setLoading, setSuccess);
      } else {
        await importContactList(opts, onSuccess);
      }

      if (onSuccess) {
        closeModal();
      }
    }
  };

  const onCsvFileParseComplete = (csvFile: File, parsedData: ParsedData) =>
    setFileData({ csvFile, parsedData });

  const onCsvFileRemove = () => {
    setFileData({ csvFile: undefined, parsedData: undefined });
    setMapping({ isValid: false, map: undefined });
  };

  const onFileReject = () => {
    dispatch(
      modalActions.openPrettyErrorModal({
        title: 'Import csv failed',
        errorMessage: `Please select csv file ${
          isSysOrCSAdmin
            ? '(10MB max)'
            : facebookPage?.free
            ? '(2MB max)'
            : '(5MB max)'
        }`,
      }),
    );
  };

  const onCsvMapUpdate = (map: IndexAttributeNameMap, isValid: boolean) =>
    setMapping({ map, isValid });

  const onAgreementToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsAgreementAccepted(e.target.checked);
  };
  const onFirstRowHeaderToggle = (isChecked: boolean) => {
    setIsFirstRowHeader(isChecked);
  };

  const isDoneButtonDisabled =
    (withMapper && !mapping.isValid) ||
    isLoading ||
    (withMapper && !isAgreementAccepted) ||
    (!withMapper && !fileData.csvFile);

  const containerClassName = classNames(
    className,
    'import-contact-list-modal',
    {
      'import-contact-list-modal--success': isSuccess,
    },
  );

  const email = session?.email || 'your email';

  return (
    <div className={containerClassName}>
      <CloseOutlined
        className="import-contact-list-modal__closeIcon"
        onClick={() => closeModal()}
      />
      {isSuccess ? (
        <OnClickElsewhere handleClick={() => closeModal()}>
          <ModalHeader
            className="import-contact-list-modal__success-message"
            withBorder={false}
          >
            <CheckmarkIcon className="import-contact-list-modal__success-icon" />
            Your contacts are being processed! Check {email} for updates.
          </ModalHeader>
          <ModalFooter
            className="import-contact-list-modal__footer"
            withBorder={false}
          >
            <PrimaryButton
              className="import-contact-list-modal__button"
              onClick={() => closeModal()}
            >
              Done
            </PrimaryButton>
          </ModalFooter>
        </OnClickElsewhere>
      ) : (
        <>
          <div className="import-contact-list-modal__header">
            <div className="import-contact-list-modal__title-container">
              <Title
                className="import-contact-list-modal__title"
                text={title}
              />
              {!!helpLink && <NeedHelpLabel link={helpLink.toString()} />}
            </div>
          </div>
          <div className="import-contact-list-modal__body">
            <div className="import-contact-list-modal__subtitle">
              Please upload the CSV file in{' '}
              <Link
                className="import-contact-list-modal__link"
                href={csvExampleBody}
                download={csvExampleName}
                isBlank
                capitalize={false}
              >
                this format.
              </Link>
            </div>
            <CSVDropParser
              onSuccess={onCsvFileParseComplete}
              onFileRemove={onCsvFileRemove}
              onFileReject={onFileReject}
              isFirstRowHeader={isFirstRowHeader}
              onHeaderCheckboxToggle={onFirstRowHeaderToggle}
              hideFirstRowDescription={!withMapper}
            />
            {fileData.parsedData && (
              <>
                {withMapper && (
                  <CSVMapper
                    columns={fileData.parsedData.meta.fields}
                    onUpdate={onCsvMapUpdate}
                  />
                )}
                {withMapper && (
                  <Checkbox
                    className="import-contact-list-modal__checkbox"
                    label={
                      <span>
                        I agree to the{' '}
                        <Link
                          to={Routes.masterServiceAgreement.getLink()}
                          className="import-contact-list-modal__link"
                        >
                          Terms of Service
                        </Link>
                        , which requires that all of my contacts have opted in
                        to receive messages from me
                      </span>
                    }
                    isChecked={isAgreementAccepted}
                    onToggle={onAgreementToggle}
                  />
                )}
              </>
            )}
          </div>
          <div className="import-contact-list-modal__footer">
            <PrimaryButton
              className={classNames(
                'import-contact-list-modal__button',
                'import-contact-list-modal__button-secondary',
              )}
              onClick={onCancelBtnClick}
              color={ButtonColor.transparent}
            >
              Cancel
            </PrimaryButton>
            <PrimaryButton
              className="import-contact-list-modal__button"
              disabled={isDoneButtonDisabled}
              onClick={onDoneButtonClick}
            >
              Done
            </PrimaryButton>
          </div>

          <ModalProcessingBlinder show={isLoading} />
        </>
      )}
    </div>
  );
};
